import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth, formatApiError } from '@/App';
import { Lightning, EnvelopeSimple, Lock, User } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/217o5tly_Marema_Logo.jpg";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      await register(formData.email, formData.password, formData.name);
      toast.success('Account created successfully!');
      navigate('/portal');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Marema Consulting" className="h-[4.5rem] w-auto" />
          </Link>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-8 shadow-sm">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight mb-2">Create Account</h2>
            <p className="text-[#4B5563]">Register for portal access</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-sm font-semibold text-[#0A192F] mb-2 block">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="John Doe"
                  className="pl-10 border-[#E5E7EB] rounded-none bg-[#F9FAFB] focus:ring-[#0055FF] focus:border-[#0055FF] h-12"
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-[#0A192F] mb-2 block">Email Address</Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="you@company.com"
                  className="pl-10 border-[#E5E7EB] rounded-none bg-[#F9FAFB] focus:ring-[#0055FF] focus:border-[#0055FF] h-12"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-[#0A192F] mb-2 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder="Min. 8 characters"
                  className="pl-10 border-[#E5E7EB] rounded-none bg-[#F9FAFB] focus:ring-[#0055FF] focus:border-[#0055FF] h-12"
                  data-testid="password-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-sm font-semibold text-[#0A192F] mb-2 block">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  placeholder="Confirm your password"
                  className="pl-10 border-[#E5E7EB] rounded-none bg-[#F9FAFB] focus:ring-[#0055FF] focus:border-[#0055FF] h-12"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none h-12 font-medium transition-transform hover:-translate-y-0.5"
              data-testid="register-submit-btn"
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-[#4B5563]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#0055FF] font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>

        <Link to="/" className="block mt-6 text-center text-[#4B5563] text-sm hover:text-[#0A192F]">
          &larr; Back to website
        </Link>
      </div>
    </div>
  );
};

export default RegisterPage;
